"use client";

import * as React from "react";
import { ArrowRight, FileText } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function InsightsCarousel() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="w-full py-12">
        <div className="flex gap-6 overflow-hidden">
          {[...Array(3)].map((_, i) => <div key={i} className="h-[300px] w-full min-w-[300px] max-w-[400px] animate-pulse rounded-xl bg-muted/10 border border-border/20" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-muted-foreground">
        Unable to load insights at this time.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        No insights available.
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {posts.map((post, i) => <CarouselItem key={post.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
            <Card className="group relative flex h-full flex-col justify-between border border-border/40 bg-card transition-colors hover:border-primary/50 rounded-none">
              <CardHeader className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="rounded-none border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary/80">
                    {post.categories?.[0]?.name || "Market Insight"}
                  </Badge>
                  <FileText className="h-4 w-4 text-muted-foreground/50" />
                </div>
                <CardTitle className="font-serif text-2xl font-medium leading-tight tracking-tight text-card-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="line-clamp-3 text-sm text-muted-foreground leading-relaxed">
                  {post.excerpt?.replace(/<[^>]*>?/gm, "") || "Strategic analysis of current market conditions and capital structuring opportunities."}
                </p>
              </CardContent>
              <CardFooter className="pt-4">
                <Link to={post.link || "#"} className="w-full">
                  <Button variant="link" className="h-auto p-0 text-sm font-medium uppercase tracking-widest text-primary hover:text-primary/80 hover:no-underline group-hover:translate-x-1 transition-transform duration-300">
                    Read Mandate <ArrowRight className="ml-2 h-3 w-3" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </CarouselItem>)}
      </CarouselContent>
      <div className="mt-8 flex justify-end gap-2 pr-4">
        <CarouselPrevious className="static translate-y-0 rounded-none border-border/40 hover:bg-accent hover:text-accent-foreground" />
        <CarouselNext className="static translate-y-0 rounded-none border-border/40 hover:bg-accent hover:text-accent-foreground" />
      </div>
    </Carousel>;
}
export default function MarketplaceInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 8,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="marketplace-insights" className="dark bg-background text-foreground py-24 border-t border-border/10">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-4">
            <h2 className="font-serif text-4xl font-medium tracking-tight text-foreground md:text-5xl">
              Marketplace Insights
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl">
              Institutional thought leadership on capital markets, private credit, and real estate structuring.
            </p>
          </div>
          <Link to="/RealEstateCapitalPage">
            <Button variant="outline" className="rounded-none border-border/40 hover:bg-accent">
              View All Insights
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <InsightsCarousel />
        </PostsProvider>
      </div>
    </section>;
}